import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {map, takeUntil, tap} from 'rxjs/operators';
import {NotificationMessages} from '../../state/notifications/notificationMessages';
import {NotificationModel, NotificationTypes} from '../../models/notification.model';
import {combineLatest, Observable, Subject} from 'rxjs';
import {NotificationsService} from '../../state/notifications/notifications.service';
import {DatePipe} from '@angular/common';
import {NotificationsQuery} from '../../state/notifications/notifications.query';
import {MapUnit} from '../../_shared/components/search-filters/state/map-units/map-unit.model';
import {ID} from '@datorama/akita';
import {HttpErrorResponse} from '@angular/common/http';
import * as _ from 'lodash';

@Component({
  selector: 'app-notifications-dropdown',
  templateUrl: './notifications-dropdown.component.html',
  styleUrls: ['./notifications-dropdown.component.scss']
})
export class NotificationsDropdownComponent implements OnInit, OnDestroy {

  @Output() navigateToStateWithParams = new EventEmitter<{ state: string, queryParams: any }>();

  private _destroyed = new Subject<void>();
  messages = NotificationMessages;
  notifications: Observable<NotificationModel[]> = this._notificationsQuery.notifications$;
  hasNotification$: Observable<boolean> = this.notifications.pipe(
    map((notifications) => !!notifications.length)
  );
  loadingNotification: boolean;

  constructor(private _notificationsService: NotificationsService,
              private _notificationsQuery: NotificationsQuery,
              private _datePipe: DatePipe) {
  }

  ngOnInit() {
    this._notificationsService.get();
    this._notificationsQuery.loading$.pipe(
      takeUntil(this._destroyed),
      tap((loading) => this.loadingNotification = loading)
    ).subscribe();
  }

  selectNotification(notification: NotificationModel) {
    if (this.loadingNotification) {
      return;
    }
    const fieldId = (notification.fieldId || notification.targetId).toString();
    switch (notification.notificationType.name) {
      case NotificationTypes.ADDITIONAL_CONSUMABLES: {
        this.openReport(notification, fieldId, 'reviews', 5);
      }
        break;
      case NotificationTypes.LOW_RATING: {
        this.openReport(notification, fieldId, 'reviews', 4);
      }
        break;
      case NotificationTypes.DISASTERS: {
        this.openReport(notification, fieldId, 'reviews', 3);
      }
        break;
      case NotificationTypes.CRITICAL_MALICIOUS: {
        this.openReport(notification, fieldId, 'reviews', 2);
      }
        break;
      case NotificationTypes.EXPIRED_SCOUT_REPORT:
      case NotificationTypes.ADDITIONAL_SCOUT_REPORT: {
        this._notificationsService.setSelectedDate(new Date(notification.createDate));
        this.navigateToStateWithParams.emit({state: 'planning', queryParams: {}});
        this._notificationsService.markAsReaded([notification.id]);
      }
        break;
      case NotificationTypes.LOW_NDVI:
      case NotificationTypes.NO_USER_FIELD_BIND: {
        this.openReport(notification, fieldId, 'map');
      }
        break;
      default: {
        this.navigateToStateWithParams.emit({state: '', queryParams: {}});
        this._notificationsService.markAsReaded([notification.id]);
      }
    }
  }

  openReport(notification: NotificationModel, fieldId: string, page: 'map' | 'reviews', selectedTab: number = 0) {
    this._notificationsService.setActiveReportTab(selectedTab);
    const savedParents = this._notificationsService.getSavedParents(fieldId);
    if (savedParents) {
      this.openReportOnPage(notification, fieldId, page, savedParents);
    } else {
      this.loadingParents(notification, fieldId, page);
    }
  }

  loadingParents(notification: NotificationModel, fieldId: string, page: 'map' | 'reviews') {
    const cropYear = this._datePipe.transform(notification.createDate, 'yyyy');
    this._notificationsService.setLoading(true);
    this._notificationsService.getMapUnitParents(fieldId, cropYear).subscribe(
      (response: MapUnit[]) => {
        this._notificationsService.setLoading(false);
        const ids = response
          .map((item: MapUnit) => item.parentId)
          .filter((id: ID) => !!id && id !== notification.id);
        const parents = _.uniq(ids)
          .reverse()
          .join(',');
        this.openReportOnPage(notification, fieldId, page, parents);
      },
      (err: HttpErrorResponse) => {
        this._notificationsService.setLoading(false);
        this._notificationsService.setError(err);
      }
    );
  }

  openReportOnPage(notification: NotificationModel, fieldId: string, page: 'map' | 'reviews', parents: string) {
    const queryParams = this.getQueryParamsByState(notification, page, parents);
    this._notificationsService.saveParents(fieldId, parents);
    this.navigateToStateWithParams.emit({state: page, queryParams: queryParams});
    this._notificationsService.markAsReaded([notification.id]);
  }

  getQueryParamsByState(notification: NotificationModel, page: 'map' | 'reviews', parents: string) {
    switch (page) {
      case 'reviews': {
        return {
          mapUnits: parents,
          lastUnit: notification.fieldId,
          year: this._datePipe.transform(notification.createDate, 'yyyy'),
          month: this._datePipe.transform(notification.createDate, 'MM'),
          reportId: notification.targetId
        };
      }
      case 'map': {
        return {
          mapUnits: parents,
          lastUnit: notification.targetId,
          cropYear: this._datePipe.transform(notification.createDate, 'yyyy')
        };
      }
    }
  }

  clearNotifications() {
    const ids: ID[] = this._notificationsQuery.getAll()
      .map((item: NotificationModel) => item.id);
    this._notificationsService.markAsReaded(ids);
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }
}
